// @ts-strict-ignore
import React, { useState, useRef, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { Icon } from '@blueprintjs/core'
import { Popover } from './popover'

export interface ComboboxOption {
    label: string
    value: string
}

interface ComboboxProps {
    options: ComboboxOption[]
    value: string
    onChange: (value: string) => void
    placeholder?: string
    /** Minimal/ghost trigger button style */
    minimal?: boolean
    className?: string
    caretAlign?: 'inline' | 'right'
}

/**
 * Searchable select / combobox. Trigger button shows the currently selected
 * option label (or a placeholder). Opening reveals a text input to filter
 * the options list.
 *
 * Intentionally lightweight — built on our existing Popover rather than a
 * full cmdk / shadcn-command setup (those aren't in the project yet).
 */
export const Combobox = ({
    options,
    value,
    onChange,
    placeholder = 'Select…',
    minimal = false,
    className,
    caretAlign = 'inline',
}: ComboboxProps) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const selectedLabel = useMemo(
        () => options.find((o) => o.value === value)?.label ?? placeholder,
        [options, value, placeholder],
    )

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return options
        return options.filter((o) => o.label.toLowerCase().includes(q))
    }, [options, query])

    // Focus the search input whenever the popover opens
    useEffect(() => {
        if (open) {
            setQuery('')
            // rAF so the popover has painted before we focus
            requestAnimationFrame(() => inputRef.current?.focus())
        }
    }, [open])

    const handleSelect = (optionValue: string) => {
        onChange(optionValue)
        setOpen(false)
    }

    const trigger = (
        <button
            type="button"
            className={classNames(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg text-[13px] font-medium transition-colors select-none',
                caretAlign === 'right' && 'justify-between',
                minimal
                    ? 'h-8 px-2.5 border border-[#DBE2E8] bg-white text-[#0E2947] hover:border-[#b0bec5]'
                    : 'h-8 px-3 border border-[#DBE2E8] bg-white text-[#0E2947] hover:border-[#b0bec5]',
                className,
            )}
        >
            <span>{selectedLabel}</span>
            <Icon icon="caret-down" size={12} color="currentColor" />
        </button>
    )

    const content = (
        <div className="py-1 w-48">
            {/* Search input */}
            <div className="px-2 pb-1">
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search…"
                    className="w-full rounded-md border border-[#DBE2E8] bg-white px-2 py-1 text-[12px] text-[#0E2947] placeholder-[#94A3B8] outline-none focus:border-[#3391FF] focus:ring-1 focus:ring-[#3391FF]"
                />
            </div>
            {/* Options list */}
            <div className="max-h-60 overflow-y-auto">
                {filtered.length === 0 ? (
                    <div className="px-3 py-2 text-[12px] text-[#94A3B8]">No results</div>
                ) : (
                    filtered.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSelect(option.value)}
                            className={classNames(
                                'bp4-popover-dismiss',
                                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] transition-colors',
                                option.value === value
                                    ? 'bg-[#EBF3FF] text-[#0E2947] font-medium'
                                    : 'text-[#1c2127] hover:bg-[rgba(143,153,168,0.15)]',
                            )}
                        >
                            {/* Checkmark spacer keeps text aligned */}
                            <span className="w-3 flex-shrink-0 text-[#3391FF]">
                                {option.value === value ? '✓' : ''}
                            </span>
                            {option.label}
                        </button>
                    ))
                )}
            </div>
        </div>
    )

    return (
        <Popover
            isOpen={open}
            onClose={() => setOpen(false)}
            content={content}
            position="bottom-right"
            minimal
            targetTagName="div"
            style={{ display: 'inline-flex' }}
        >
            <span onClick={() => setOpen((prev) => !prev)}>{trigger}</span>
        </Popover>
    )
}
